import { useRef } from 'react'
import GoogleMapReact from 'google-map-react'
import { withHandlers } from 'recompose'
import * as selectors from 'map/selectors'
import { setActiveLocation, setMapBounds } from 'map/actions'
import ClusterLocationsMarker from 'map/components/cluster/ClusterLocationsMarker'
import CarParkMarker from 'map/components/markers/CarParkMarker'
import { defaultStyle as style } from 'shared/generate-marker-style'

import styles from './styles.module.scss'

const Map = ({ onMapChange, zoom, center, onMapClick, inactivePoints, activePoint, activeSearchLocation }) => {
    const googleMarker = useRef(null)
    const googleAPI = useRef(null)
    const googleMapRef = useRef(null)

    const renderLocationsPoints = () =>
        inactivePoints.map(
            (
                {
                    properties: { id, cluster, cluster_id, point_count, hovered },
                    geometry: {
                        coordinates: [lng, lat],
                    },
                },
                index
            ) =>
                cluster ? (
                    <ClusterLocationsMarker
                        style={style}
                        lat={lat}
                        lng={lng}
                        cluster_id={cluster_id}
                        pointCount={point_count}
                        key={index}
                        hovered={!!hovered}
                    />
                ) : (
                    <CarParkMarker
                        style={style}
                        faded={activePoint && activePoint.length}
                        lat={lat}
                        lng={lng}
                        sfLocationId={id}
                        key={id}
                    />
                )
        )

    const renderActivePoint = () =>
        activePoint.map(
            ({
                properties: { id },
                geometry: {
                    coordinates: [lng, lat],
                },
            }) => <CarParkMarker style={style} lat={lat} lng={lng} sfLocationId={id} key={id} />
        )

    const renderGoogleLocationMarker = () => {
        if (!googleAPI.current) return

        const { map, maps } = googleAPI.current
        const { lng, lat, title } = activeSearchLocation

        if (googleMarker.current) {
            const currentLat = googleMarker.current.getPosition().lat() === lat
            const currentLng = googleMarker.current.getPosition().lng() === lng

            if (currentLat && currentLng) return

            // removes existing marker
            googleMarker.current.setMap(null)
            googleMarker.current = null
        }

        const marker = new maps.Marker({
            position: { lng, lat },
            map,
            title,
        })

        googleMarker.current = marker
    }

    if (activeSearchLocation) {
        renderGoogleLocationMarker()
    }

    return (
        <div className={styles.map}>
            <div style={{ top: 0, bottom: 0, left: 0, right: 0, position: 'absolute' }}>
                <GoogleMapReact
                    ref={googleAPI}
                    center={center}
                    debounced={true}
                    layerTypes={[]}
                    onChange={onMapChange}
                    onClick={onMapClick}
                    resetBoundsOnResize={false}
                    zoom={zoom}
                    onGoogleApiLoaded={({ map, maps }) => (googleAPI.current = { map, maps })}>
                    {renderLocationsPoints()}
                    {renderActivePoint()}
                </GoogleMapReact>
            </div>
        </div>
    )
}

export default compose(
    withTranslate,
    connect(
        createStructuredSelector({
            activePoint: selectors.activePointSelector,
            center: selectors.centerSelector,
            inactivePoints: selectors.inactivePointsSelector,
            zoom: selectors.zoomSelector,
            activeSearchLocation: selectors.activeSearchLocationSelector,
        }),
        {
            setActiveLocation,
            setMapBounds,
        }
    ),
    withHandlers({
        onMapChange:
            ({ setMapBounds }) =>
            ({ center, zoom, bounds }) =>
                setMapBounds(center, zoom, bounds),
        onMapClick:
            ({ setActiveLocation }) =>
            () =>
                setActiveLocation(null),
    })
)(Map)
